import { ErrorMessage } from '@hookform/error-message';
import { Alert, Container } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import VyButton from '../components/common/VyButton';
import VyTextField from '../components/common/VyTextField';
import { EventInterface } from '../interfaces/interfaces';
import { createDisplayLink } from '../services/helper';
import { authLogin } from '../services/services';
import { eventLogin, login } from '../utils/auth';
import { useSelector } from '../reduxToolkit/typedRedux';
import useRegistration from '../hooks/useRegistration';

const EventPasswordLogin = ({ event }: { event: EventInterface }) => {
  const user = useSelector((state) => state.common.user);
  const [msg, setMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const router = useRouter();
  const {
    formState: { errors },
    getValues,
    handleSubmit,
    control,
    setValue,
  } = useForm();
  const { i18n, t } = useTranslation();
  const { isEventVystemEmail } = useRegistration();

  function redirect() {
    router.push(`/event/${event?.slug}/live`);
  }

  async function submit() {
    setIsLoading(true);
    const values = getValues();
    const data = {
      email: values.email,
      password: values.password,
      eventPasswordLoginId: event._id,
    };
    try {
      const res = await authLogin(data);
      const { role, access_token } = res.data;
      login(access_token, role.level.toString());
      eventLogin(event._id);
      redirect();
    } catch (e: any) {
      setIsLoading(false);
      setMsg(e?.response?.data?.translation?.[`${i18n.language}-${i18n.language?.toUpperCase()}`] || t('common:error-without-status'));
    }
  }

  useEffect(() => {
    if (!user) return;
    if (event.modules.guestmanagement.settings.authRandomUser && isEventVystemEmail(user.email)) {
      setValue('email', user.email);
    }
  }, [event, user]);

  return (
    <Container component="main" maxWidth="xs">
      <div style={{ paddingTop: '15%' }} className="flex flex-col items-center justify-center space-y-10">
        <img src={createDisplayLink(event.template.logo)} style={{ maxWidth: '20%' }} alt="event logo" />
        <Alert className="w-full" severity="info">
          {t('event:event-password-protected')}
        </Alert>
        <form className="space-y-4" onSubmit={handleSubmit(submit)} noValidate>
          {!event.modules.guestmanagement.settings.authRandomUser && (
            <>
              <Controller
                control={control}
                name={`email`}
                rules={{
                  required: `${t('form:fieldrequired')}`,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                    message: `${t('account:invalid-email')}`,
                  },
                }}
                render={({ field }) => (
                  <VyTextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label={t('auth:email')}
                    type="email"
                    autoComplete="email"
                    color="secondary"
                    autoFocus
                  />
                )}
              />
              <ErrorMessage errors={errors} name="email" />
            </>
          )}
          <Controller
            control={control}
            name={`password`}
            rules={{ required: `${t('event:required-field-error')}` }}
            render={({ field }) => (
              <VyTextField
                {...field}
                margin="normal"
                required
                fullWidth
                label={t('auth:password')}
                type="password"
                id="password"
                color="secondary"
                autoComplete="current-password"
              />
            )}
          />
          <ErrorMessage errors={errors} name="password" />
          <VyButton event={event} disabled={isLoading} className="w-full" id="login" type="submit">
            {t('auth:login')}
          </VyButton>
          <p id="error" className="text-center">
            {msg}
          </p>
        </form>
      </div>
    </Container>
  );
};

export default EventPasswordLogin;
